import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { ToolLogo } from "@/components/ToolLogo";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, RotateCcw } from "lucide-react";

export default function Quiz() {
  const [quiz, setQuiz] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/quiz").then(r => setQuiz(r.data)); }, []);

  const answer = (question, option) => {
    const next = { ...answers };
    if (question.id === "q1") next.q1_categories = option.categories;
    else if (question.id === "q2") next.q2_level = option.level;
    else if (question.id === "q3") next.q3_budget = option.budget;
    setAnswers(next);
    if (step < quiz.length - 1) setStep(step + 1);
    else finish(next);
  };

  const finish = async (final) => {
    setLoading(true);
    try {
      const r = await api.post("/quiz/recommend", { answers: final });
      setResults(r.data.recommended);
    } finally { setLoading(false); }
  };

  const restart = () => { setAnswers({}); setStep(0); setResults(null); };

  if (!quiz.length) return <Layout hideRightPanel><div className="skeleton h-40 mt-8" /></Layout>;
  const current = quiz[step];

  return (
    <Layout hideRightPanel>
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-coral font-bold">Onboarding</div>
        <h1 className="font-display text-5xl font-bold mt-2">Not sure where to start?</h1>
        <p className="text-[var(--curio-text-muted)] mt-3 max-w-xl">Three quick questions — get a personalized AI stack tailored to your goals and budget.</p>
      </header>

      {!results ? (
        <div className="curio-card p-8 max-w-2xl" data-testid="quiz-card">
          <div className="flex justify-between text-xs uppercase tracking-widest text-[var(--curio-text-muted)] font-bold mb-4">
            <span>Question {step + 1} of {quiz.length}</span>
            <span>{Math.round(((step + 1) / quiz.length) * 100)}%</span>
          </div>
          <div className="h-1 bg-[var(--curio-bg)] rounded-full overflow-hidden mb-6">
            <div className="h-full bg-[var(--curio-coral)] transition-all" style={{ width: `${((step + 1) / quiz.length) * 100}%` }} />
          </div>
          <h2 className="font-display text-3xl font-bold mb-6">{current.question}</h2>
          <div className="space-y-2">
            {current.options.map((o, i) => (
              <button key={o.label} onClick={() => answer(current, o)}
                className="w-full text-left p-4 rounded-xl border border-[var(--curio-border)] hover:border-[var(--curio-navy)] hover:bg-[var(--curio-bg)] transition-all flex items-center justify-between group"
                data-testid={`quiz-option-${current.id}-${i}`}>
                <span>{o.label}</span>
                <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="quiz-results">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-3xl font-bold">Your recommended stack</h2>
            <button onClick={restart} className="btn-ghost text-sm" data-testid="quiz-restart"><RotateCcw size={13} /> Retake</button>
          </div>
          {loading ? <div className="skeleton h-40" /> : results.length === 0 ? (
            <p className="text-[var(--curio-text-muted)]">No exact matches — try widening your budget.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger">
              {results.map(t => (
                <Link key={t.slug} to={`/tool/${t.slug}`} className="curio-card p-5" data-testid={`quiz-rec-${t.slug}`}>
                  <ToolLogo tool={t} size={40} rounded="lg" className="mb-2" />
                  <div className="text-[10px] uppercase tracking-widest text-[var(--curio-text-muted)] font-bold">{t.category}</div>
                  <h3 className="font-display text-2xl font-semibold mt-1 mb-2">{t.name}</h3>
                  <p className="text-sm text-[var(--curio-text)]/85 mb-3">{t.tagline}</p>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-bold">Wikifi {t.curio_score}</span>
                    <span className="font-bold text-coral">VALUE {t.value_score}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <div className="mt-8 curio-navy-bg rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-coral font-bold mb-2 flex items-center gap-2"><Sparkles size={13} /> Next step</div>
            <p className="text-lg">Save these to <Link to="/favorites" className="text-coral underline">My AI Stack</Link> and start with the highest-scoring one.</p>
          </div>
        </div>
      )}
    </Layout>
  );
}
